/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Sparkles, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, ErrorText, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney } from '@/lib/format';
import { STRATEGY } from '@kd/shared';

const CH: Record<string, string> = { CALL: 'Звонок', SMS: 'SMS', EMAIL: 'Почта', MEETING: 'Встреча', LETTER: 'Письмо', MESSENGER: 'Мессенджер' };
const STRAT: Record<string, string> = { PRETRIAL: 'Досудебно', SETTLEMENT: 'Мировое / рассрочка', COURT: 'Суд', BANKRUPTCY: 'Банкротство', CESSION: 'Цессия', WRITEOFF: 'Списание' };
const tone = (s: number) => (s >= 60 ? 'green' : s >= 30 ? 'amber' : 'red') as 'green' | 'amber' | 'red';

/** M19 Скоринг вероятности взыскания */
export function ScorePanel({ debtId }: { debtId: string }) {
  const qc = useQueryClient();
  const { data: hist } = useQuery({ queryKey: ['score', debtId], queryFn: () => api.get<any[]>(`/scoring/debts/${debtId}`) });
  const run = useMutation({ mutationFn: (ai: boolean) => api.post(`/scoring/debts/${debtId}${ai ? '?ai=1' : ''}`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ['score', debtId] }) });
  const last: any = run.data ?? hist?.[0];
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium"><TrendingUp size={16} />Вероятность взыскания</div>
        <div className="flex gap-1">
          <Button size="sm" variant="secondary" disabled={run.isPending} onClick={() => run.mutate(false)}>Оценить</Button>
          <Button size="sm" variant="ghost" disabled={run.isPending} onClick={() => run.mutate(true)}><Sparkles size={14} />С комментарием ИИ</Button>
        </div>
      </div>
      <ErrorText error={run.error} />
      {!last && <div className="text-sm text-slate-500">Оценка ещё не проводилась</div>}
      {last && <>
        <div className="mb-2 flex items-baseline gap-3">
          <span className="text-3xl font-semibold tabular-nums">{last.score}%</span>
          <Badge tone={tone(last.score)}>{STRAT[last.strategy] ?? last.strategy}</Badge>
          <span className="text-sm text-slate-500">ожидаемо {fmtMoney(last.expected)}</span>
        </div>
        <div className="mb-2 text-sm">{STRATEGY[last.strategy]}
        </div>
        <ul className="space-y-1 text-sm">
          {(last.factors ?? []).map((f: any, i: number) => (
            <li key={i} className="flex gap-2"><span className={`w-10 shrink-0 text-right tabular-nums ${f.impact > 0 ? 'text-green-700' : f.impact < 0 ? 'text-red-700' : 'text-slate-500'}`}>{f.impact > 0 ? '+' : ''}{f.impact}</span><span><b className="font-medium">{f.name}</b>: {f.note}</span></li>
          ))}
        </ul>
        {last.comment && <div className="mt-3 whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm">{last.comment}</div>}
        <div className="mt-2 text-xs text-slate-400">Оценка от {fmtDate(last.createdAt)}. Результат носит справочный характер, решение принимает юрист.</div>
      </>}
    </div>
  );
}

/** M23 Журнал контактов с должником, контроль 230-ФЗ, анализ разговоров и стратегия */
export function CollectionPanel({ debtId, debtorPerson }: { debtId: string; debtorPerson: boolean }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ channel: 'CALL', direction: 'OUT', text: '', result: '', promiseAmount: '', promiseDate: '', durationSec: '' });
  const [force, setForce] = useState(false);
  const { data: list } = useQuery({ queryKey: ['contacts', debtId], queryFn: () => api.get<any[]>(`/collection/debts/${debtId}/contacts`) });
  const { data: chk } = useQuery({ queryKey: ['can-contact', debtId, f.channel], queryFn: () => api.get<any>(`/collection/debts/${debtId}/can-contact?channel=${f.channel}`), enabled: open && debtorPerson });
  const { data: plan, refetch: rePlan, isFetching: planning } = useQuery({ queryKey: ['plan', debtId], queryFn: () => api.get<any>(`/collection/debts/${debtId}/plan`) });
  const [aiPlan, setAiPlan] = useState<string | null>(null);
  const inv = () => qc.invalidateQueries({ queryKey: ['contacts', debtId] });
  const add = useMutation({
    mutationFn: () => api.post(`/collection/debts/${debtId}/contacts`, {
      channel: f.channel, direction: f.direction, text: f.text || undefined, result: f.result || undefined,
      promiseAmount: f.promiseAmount ? f.promiseAmount.replace(',', '.') : undefined, promiseDate: f.promiseDate || undefined,
      durationSec: f.durationSec ? Number(f.durationSec) : undefined, force: force || undefined,
    }),
    onSuccess: () => { setOpen(false); setForce(false); setF({ ...f, text: '', result: '', promiseAmount: '', promiseDate: '', durationSec: '' }); inv(); },
  });
  const res = useMutation({ mutationFn: ({ id, r }: { id: string; r: string }) => api.post(`/collection/contacts/${id}/result`, { result: r }), onSuccess: inv });
  const analyze = useMutation({ mutationFn: (id: string) => api.post(`/collection/contacts/${id}/analyze`, {}), onSuccess: inv });
  const strategy = useMutation({ mutationFn: () => api.get<any>(`/collection/debts/${debtId}/plan?ai=1`), onSuccess: (r) => setAiPlan(r.strategy) });
  const w = can('debt.write');
  const violations: any[] = chk?.violations ?? [];
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium"><MessageSquare size={16} />Контакты с должником</div>
        {w && <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Добавить контакт</Button>}
      </div>
      <div className="mb-3 rounded bg-slate-50 p-2 text-sm">
        <div>{plan?.note ?? (planning ? 'Анализ истории...' : '')}</div>
        {aiPlan && <div className="mt-2 whitespace-pre-wrap">{aiPlan}</div>}
        <div className="mt-1 flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => rePlan()}>Обновить</Button>
          <Button size="sm" variant="ghost" disabled={strategy.isPending} onClick={() => strategy.mutate()}><Sparkles size={14} />Стратегия ИИ</Button>
        </div>
        <ErrorText error={strategy.error} />
      </div>
      <ul className="divide-y divide-slate-100 text-sm">
        {(list ?? []).map((c) => (
          <li key={c.id} className="py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-slate-500">{new Date(c.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}</span>
              <Badge>{CH[c.channel] ?? c.channel}{c.direction === 'IN' ? ' (вх.)' : ''}</Badge>
              {c.sentiment && <Badge tone={/агресс|негат/.test(c.sentiment) ? 'red' : 'slate'}>{c.sentiment}</Badge>}
              {c.violations?.length > 0 && <Badge tone="red">нарушения: {c.violations.length}</Badge>}
              {c.promiseDate && <Badge tone={c.result === 'KEPT' ? 'green' : c.result === 'BROKEN' ? 'red' : 'amber'}>обещал {c.promiseAmount ? fmtMoney(c.promiseAmount) : ''} до {fmtDate(c.promiseDate)}</Badge>}
            </div>
            {c.text && <div className="mt-1 line-clamp-3 text-slate-700">{c.text}</div>}
            {c.analysis?.summary && <div className="mt-1 text-slate-600"><b className="font-medium">Анализ:</b> {c.analysis.summary}{c.analysis.nextStep ? ` Далее: ${c.analysis.nextStep}` : ''}</div>}
            {c.violations?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-red-700">{c.violations.map((v: any, i: number) => <li key={i}>{v.text ?? v}</li>)}</ul>}
            {w && <div className="mt-1 flex gap-2">
              {c.text && !c.analysis && <Button size="sm" variant="ghost" disabled={analyze.isPending} onClick={() => analyze.mutate(c.id)}><Sparkles size={14} />Проанализировать</Button>}
              {c.promiseDate && !c.result && <>
                <Button size="sm" variant="ghost" onClick={() => res.mutate({ id: c.id, r: 'KEPT' })}>Исполнено</Button>
                <Button size="sm" variant="ghost" onClick={() => res.mutate({ id: c.id, r: 'BROKEN' })}>Нарушено</Button>
              </>}
            </div>}
          </li>
        ))}
        {!list?.length && <li className="py-2 text-slate-500">Контактов пока нет</li>}
      </ul>
      <ErrorText error={analyze.error} />
      <Modal title="Контакт с должником" open={open} onClose={() => setOpen(false)}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Способ"><Select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} options={Object.entries(CH).map(([value, label]) => ({ value, label }))} /></Field>
            <Field label="Направление"><Select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} options={[{ value: 'OUT', label: 'Исходящий' }, { value: 'IN', label: 'Входящий' }]} /></Field>
          </div>
          {debtorPerson && f.direction === 'OUT' && (violations.length
            ? <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800"><b>Ограничения 230-ФЗ:</b><ul className="list-disc pl-5">{violations.map((v, i) => <li key={i}>{v.text}</li>)}</ul></div>
            : <div className="rounded bg-green-50 p-2 text-sm text-green-800">Ограничения 230-ФЗ сейчас не нарушаются</div>)}
          <Field label="Текст разговора или переписки" hint="Проверяется на запрещённые формулировки"><Textarea rows={5} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Длительность, сек"><Input value={f.durationSec} onChange={(e) => setF({ ...f, durationSec: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Обещал оплатить, руб."><Input value={f.promiseAmount} onChange={(e) => setF({ ...f, promiseAmount: e.target.value })} /></Field>
            <Field label="до"><Input type="date" value={f.promiseDate} onChange={(e) => setF({ ...f, promiseDate: e.target.value })} /></Field>
          </div>
          <Field label="Итог"><Input value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} placeholder="например, не дозвонился" /></Field>
          {violations.length > 0 && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />Контакт уже состоялся, зафиксировать с отметкой о нарушении</label>}
          <ErrorText error={add.error} />
          <Button disabled={add.isPending} onClick={() => add.mutate()}>Сохранить</Button>
        </div>
      </Modal>
    </div>
  );
}

/** M25 Отправка SMS лицу (с подстановкой {name}, {debt}, {hearing}) */
export function SmsButton({ partyId, debtId, size = 'md' }: { partyId: string; debtId?: string; size?: 'sm' | 'md' }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const { data: tpl } = useQuery({ queryKey: ['sms-templates'], queryFn: () => api.get<any[]>('/sms/templates'), enabled: open });
  const send = useMutation({ mutationFn: () => api.post<any>('/sms/send', { partyId, debtId, body }) });
  const r = send.data;
  return (
    <>
      <Button size={size} variant="secondary" onClick={() => { setOpen(true); send.reset(); }}><MessageSquare size={16} />SMS</Button>
      <Modal title="Отправить SMS" open={open} onClose={() => setOpen(false)}>
        <div className="space-y-3">
          {!!tpl?.length && <Field label="Шаблон"><Select placeholder="без шаблона" value="" onChange={(e) => setBody(tpl.find((t) => t.id === e.target.value)?.body ?? body)} options={tpl.map((t) => ({ value: t.id, label: t.name }))} /></Field>}
          <Field label="Текст" hint={`Подстановки: {name}${debtId ? ', {debt}' : ''}, {hearing}, {case}, {court}. ${body.length} симв.`}><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
          {r && <div className={`rounded p-2 text-sm ${r.status === 'SKIPPED' ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>{r.status === 'SKIPPED' ? `Не поставлено: ${r.reason}` : `Поставлено в очередь (${r.phone})${r.status === 'QUEUED' ? ', будет отправлено вне тихих часов' : ''}`}</div>}
          <ErrorText error={send.error} />
          <Button disabled={!body.trim() || send.isPending} onClick={() => send.mutate()}>Отправить</Button>
        </div>
      </Modal>
    </>
  );
}
